import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import questionnaireForm from '../../components/SellerProfile/Form/json/questionnaireFormFields.json';
import _ from 'lodash';

export interface QuestionnaireState {
  questionnaireForm: any;
  surveyDetails: any[];
  loading: boolean;
  error: string | null;
  submissionSuccess: boolean;
  postQuestionnaire: boolean,
  loaded: boolean,
  submitSuccess: boolean
}

const initialState: QuestionnaireState = {
  questionnaireForm: {},
  surveyDetails: [],
  loading: false,
  error: null,
  submissionSuccess: false,
  postQuestionnaire: false,
  loaded: false,
  submitSuccess: false

};

const doOptions = (apiData, newEntry, parent) => {
  apiData.split('//').forEach((subQ, i) => {
    const subFields = subQ.split('|');
    const subQcontent: any = {};

    subFields.forEach((field) => {
      const finalContent = field.split(':');
      subQcontent[finalContent[0]] = finalContent[1];
    });
    newEntry['options'][subQcontent['survey_option_id']] = {
      label: subQcontent['survey_options_description'],
      type: 'checkbox',
      checked: false,
      parent: parent,
      optional: true,
      id: subQcontent['survey_option_id']
    };
  });
};

const doAnsOptions = (apiData, ans, newEntry, parent) => {
  apiData.split('//').forEach((subQ, i) => {
    const subFields = subQ.split('|');
    const subQcontent: any = {};

    subFields.forEach((field) => {
      const finalContent = field.split(':');
      subQcontent[finalContent[0]] = finalContent[1];

      const regex = new RegExp(`\\b\\w*OPTS\\w*\\b`, 'g');
      const match = finalContent[1].match(regex);
      if (match) {
        const res = ans.split(',').includes(match[0]);
        subQcontent['checked'] = res;
      }
    });

    newEntry['options'][subQcontent['survey_option_id']] = {
      label: subQcontent['survey_options_description'],
      type: 'checkbox',
      checked: subQcontent['checked'],
      parent: parent,
      optional: true,
      id: subQcontent['survey_option_id']
    };
  });
};

const doSubQuestions = (apiData, parentKey, questionnaireForm, subAnswer) => {
  questionnaireForm[`${parentKey}-SUB`] = { type: 'group', options: {} };
  const currEntry = questionnaireForm[`${parentKey}-SUB`]['options'];

  apiData.split('//').forEach((subQ, i) => {
    const subFields = subQ.split('|');
    const subQcontent: any = {};
    subFields.forEach((field) => {
      const finalContent = field.split(':');
      subQcontent[finalContent[0]] = finalContent[1];
    });
    let subA = null;
    if (subAnswer) {
      subAnswer.split('//').map((field) => {
        if (field.includes(subQcontent['survey_sub_question_id'])) {
          subA = field.split('|')[1].split(':')[1];
        }
        return null;
      });
    }

    currEntry[subQcontent['survey_sub_question_id']] = {
      label: subQcontent['survey_sub_question_desc'],
      type: 'text',
      required: 'false',
      validate: 'alphanumeric',
      content: subA,
      example:
        subQcontent['additional_info'] !== 'null'
          ? subQcontent['additional_info']
          : ''
    };
  });
};


const questionnaireSlice = createSlice({
  name: 'questionnaire',
  initialState,
  reducers: {
    fetchQuestionnaireRequest(state, action: PayloadAction<{ language: string }>) {
      state.loading = true;
    },
    fetchQuestionnaireSuccess(state, action: PayloadAction<any>) {
      state.loading = false;
      const { questions, language } = action.payload;
      state.surveyDetails = questions;
      state.questionnaireForm = _.cloneDeep(questionnaireForm);
      const setValsByObjKey = (apiData, qForm, language) => {
        const field = apiData['survey_question_id'];

        const sub_question = language === 'fr' ? 'sub_question_fr' : 'sub_question_en';
        const survey_question_description = language === 'fr' ? 'survey_question_description_fr' : 'survey_question_description_en';
        const additional_info = language === 'fr' ? 'additional_info_fr' : 'additional_info_en';
        const options = language === 'fr' ? 'options_fr' : 'options_en';

        if (apiData['survey_question_id']) {
          Object.keys(qForm).forEach((key) => {
            if (
              typeof qForm[key] === 'object' &&
              qForm[key] !== null &&
              key !== field
            ) {
              setValsByObjKey(apiData, qForm[key], language);
            } else if (key === field) {
              qForm[key]['label'] = apiData[survey_question_description];
              qForm[key]['required'] =
                apiData['optional'] === 1 ? 'false' : 'true';
              qForm[key]['example'] = apiData[additional_info];

              if (apiData[options] && apiData[options] !== null) {
                doOptions(apiData[options], qForm[key], key);
                apiData['parentArray'] = true;
                apiData['type'] = 'group';
              } else {
                apiData['type'] = 'text';
              }
              apiData[sub_question] &&
                apiData[sub_question] !== null &&
                doSubQuestions(
                  apiData[sub_question],
                  key,
                  state.questionnaireForm,
                  null
                );
            }
          });
        }
      }
      questions.forEach((apiQ) => {
        apiQ['survey_question_id'].includes('QUE') &&
          setValsByObjKey(apiQ, state.questionnaireForm, language);
      });
    },

    fetchQuestionnaireError(state, action: PayloadAction<string>) {
      state.error = action.payload;
      state.loading = false
    },
    setSurveyDetails(state, action: PayloadAction<any>) {
      state.surveyDetails = action.payload;
    },
    submitQuestionnaireRequest(state, action: PayloadAction<any>) {
      state.loading = true;
      state.submissionSuccess = false;
    },
    submitQuestionnaireSuccess(state, action: PayloadAction<any>) {
      state.loading = false;
      state.submissionSuccess = true;
    },
    submitQuestionnaireError(state, action: PayloadAction<string>) {
      state.error = action.payload;
      state.loading = false;
      state.submissionSuccess = false;
    },
    QuestionnaireWithAnswerStart(state, action: PayloadAction<{ language: string, userId: string }>) {
    },
    QuestionnaireWithAnswerSuccess(state, action: PayloadAction<any>) {
      const { questions, language } = action.payload;
      state.surveyDetails = questions;
      state.questionnaireForm = _.cloneDeep(questionnaireForm);

      //parse API data to show in form
      const setValsByObjKey = (apiData, qForm, language) => {
        const field = apiData['survey_question_id'];

        const sub_question = language === 'fr'
          ? 'sub_question_fr'
          : 'sub_question_en';
        const user_answer = language === 'fr' ? 'user_answer' : 'user_answer';
        const survey_question_description = language === 'fr'
          ? 'survey_question_description_fr'
          : 'survey_question_description_en';
        const additional_info = language === 'fr'
          ? 'additional_info_fr'
          : 'additional_info_en';
        const options = language === 'fr' ? 'options_fr' : 'options_en';

        if (apiData['survey_question_id']) {
          Object.keys(qForm).forEach((key) => {
            if (
              typeof qForm[key] === 'object' &&
              qForm[key] !== null &&
              key !== field
            ) {
              setValsByObjKey(apiData, qForm[key], language);
            } else if (key === field) {
              qForm[key]['content'] = apiData[user_answer] || '';
              qForm[key]['label'] = apiData[survey_question_description];
              // qForm[key]["required"] = apiData["optional"] === 1 ? "false" : "true";
              qForm[key]['example'] = language === 'fr'
                ? apiData[additional_info]
                : apiData[additional_info];

              if (apiData[options] && apiData[options] !== null) {
                doAnsOptions(
                  apiData[options],
                  apiData['options'],
                  qForm[key],
                  key
                );
                apiData['parentArray'] = true;
                apiData['type'] = 'group';
              } else {
                apiData['type'] = 'text';
              }

              if (qForm[key]['type'] === 'radio') {
                Object.entries(qForm[key].options).forEach(
                  ([key, value]: any) => {
                    if (
                      value[`name`].toLowerCase() ===
                      apiData[user_answer].toLowerCase()
                    ) {
                      value.checked = true;
                    } else {
                      value.checked = false;
                    }
                  }
                );
              }

              apiData[sub_question] &&
                apiData[sub_question] !== null &&
                doSubQuestions(
                  apiData[sub_question],
                  key,
                  state.questionnaireForm,
                  apiData[`sub_answer`]
                );

            }
          });
        }
      };
      questions.forEach((apiQ) => {
        apiQ['survey_question_id'].includes('QUE') &&
          setValsByObjKey(apiQ, state.questionnaireForm, language);
      });
    },
    QuestionnaireWithAnswerFailure(state, action: PayloadAction<any>) {

    },
    SpQAPIsubmitQuestionnaireStart(state, action: PayloadAction<any>) {
      state.loading = true;
    },
    SpQAPIsubmitQuestionnaireSuccess(state, action: PayloadAction<any>) {
      state.loading = false;
    },
    SpQAPIsubmitQuestionnaireFailure(state, action: PayloadAction<any>) {
      state.loading = false;
    },
    setSubmitSuccess(state, action: PayloadAction<boolean>) {
      state.submitSuccess = action.payload
    }
  }
});

export const {
  fetchQuestionnaireRequest,
  fetchQuestionnaireSuccess,
  fetchQuestionnaireError,
  setSurveyDetails,
  submitQuestionnaireRequest,
  submitQuestionnaireSuccess,
  submitQuestionnaireError,
  QuestionnaireWithAnswerFailure,
  QuestionnaireWithAnswerStart,
  QuestionnaireWithAnswerSuccess,
  SpQAPIsubmitQuestionnaireFailure,
  SpQAPIsubmitQuestionnaireStart,
  SpQAPIsubmitQuestionnaireSuccess,
  setSubmitSuccess
} = questionnaireSlice.actions;


export default questionnaireSlice.reducer;